import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

type ViewMode = 'admin' | 'teacher' | 'support_staff';

interface ViewSwitcherProps {
  currentMode: ViewMode;
  onSwitch: (mode: ViewMode) => void;
  dropUp?: boolean;
}

const VIEW_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'support_staff', label: 'Support Staff' },
];

/**
 * View mode switcher for admin users.
 * Allows admins to toggle between Admin, Teacher, and Support Staff views.
 */
export function ViewSwitcher({ currentMode, onSwitch, dropUp = false }: ViewSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<ViewMode | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const currentLabel = VIEW_OPTIONS.find((o) => o.value === currentMode)?.label || 'Admin';

  const handleSelect = (mode: ViewMode) => {
    onSwitch(mode);
    setIsOpen(false);
  };

  const getOptionBackground = (optionValue: ViewMode): string => {
    const isSelected = optionValue === currentMode;
    const isHovered = optionValue === hoveredOption;

    if (isSelected) {
      return 'var(--primary-color-light)';
    }
    if (isHovered) {
      return 'var(--surface-color)';
    }
    return 'transparent';
  };

  return (
    <div ref={dropdownRef} style={styles.container}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label="Switch view mode"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        style={styles.trigger}
      >
        <span style={styles.triggerLabel}>View:</span>
        <span style={styles.triggerValue}>{currentLabel}</span>
        <ChevronDown
          size={14}
          style={{
            ...styles.chevron,
            transform: dropUp
              ? isOpen
                ? 'rotate(0deg)'
                : 'rotate(180deg)'
              : isOpen
                ? 'rotate(180deg)'
                : 'rotate(0deg)',
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            ...styles.dropdown,
            ...(dropUp
              ? {
                  top: 'auto',
                  bottom: 'calc(100% + 4px)',
                  animation: 'fadeInDropUp 0.15s ease',
                }
              : {}),
          }}
          role="listbox"
          aria-label="View mode options"
        >
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option.value);
              }}
              onMouseEnter={() => setHoveredOption(option.value)}
              onMouseLeave={() => setHoveredOption(null)}
              role="option"
              aria-selected={option.value === currentMode}
              style={{
                ...styles.option,
                backgroundColor: getOptionBackground(option.value),
              }}
            >
              <span style={styles.optionLabel}>{option.label}</span>
              {option.value === currentMode && <Check size={14} style={styles.checkIcon} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.375rem 0.5rem',
    backgroundColor: 'var(--background-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: '0.75rem',
    color: 'var(--text-primary)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    fontFamily: 'inherit',
  },
  triggerLabel: {
    color: 'var(--text-secondary)',
    fontWeight: '400',
  },
  triggerValue: {
    fontWeight: '500',
  },
  chevron: {
    color: 'var(--text-secondary)',
    marginLeft: '0.125rem',
    transition: 'transform 0.2s ease',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    right: '0',
    minWidth: '140px',
    backgroundColor: 'var(--background-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-lg)',
    padding: '0.25rem',
    zIndex: 1000,
    animation: 'fadeInDown 0.15s ease',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    color: 'var(--text-primary)',
    textAlign: 'left',
    transition: 'background-color 0.1s ease',
    fontFamily: 'inherit',
  },
  optionLabel: {
    fontWeight: '400',
  },
  checkIcon: {
    color: 'var(--primary-color)',
    flexShrink: 0,
  },
};
